import flatpickr from "flatpickr";
import { German } from "flatpickr/dist/l10n/de.js";

/**
 * "Zählerstand melden" — personalized-link meter reading landing page.
 *
 *   - read v (Vertragsnummer) + z (Zählernummer) from the URL
 *   - getPossibleMetersByContractNumber -> meter number + OBIS registers
 *   - compare with z: match -> form; mismatch -> "Zählerwechsel?" popup
 *   - loadContract -> periodStart (Ablesedatum lower bound)
 *   - form: single OBIS 1.8.0  vs  HT/NT OBIS 1.8.1 + 1.8.2
 *   - createMeterReading(forceSave=false): [] -> success page;
 *     "not plausible" -> "Bist Du sicher?" popup -> forceSave=true ->
 *     "Zählerwechsel?" popup
 *   - "Zählerwechsel?" -> Ja/Nein -> photo-upload sub-page -> e-mail to the MBS,
 *     or (mismatch + Nein) -> "Zählernummer passt nicht" error screen
 */

const SELECTORS = {
    ALL_INPUTS: '.ninja-forms-field:not(.select2-hidden-accessible)',
    FIELD_CONTAINER: '.nf-field-container',
    NINJA_FORMS_FIELD: '.ninja-forms-field',
    DATE_PICKER: '.date-container .datepicker',
    DATE_LABEL: '.date-container label',
    DATE_ICON: '.date-icon',
    ABLESEDATUM_INPUT: '[name="ablesedatum"]',
    METER_READING_INPUT: '[name="meterReading"]',
    METER_READING_HT_INPUT: '[name="meterReadingHT"]',
    METER_READING_NT_INPUT: '[name="meterReadingNT"]',
    FORM: '#zaehlerstand-form',
    UPLOAD_FORM: '#zaehlerstand-upload-form',
    FOTODATUM_INPUT: '[name="fotodatum"]',
    FORM_CONTAINER: '.form-box',
    LOADER: '#loader',
    RESPONSE_ERRORS: '.response-errors',
    SUBMIT_BUTTON: '[type="submit"]',
};

const UPLOAD_ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];
const UPLOAD_MAX_SIZE = 20 * 1024 * 1024;

const CFG = (typeof window !== 'undefined' && window.zaehlerstand_melden) || {};

class ZaehlerstandMelden {
    constructor() {
        this._loading = false;
        this.onLoadingChange = null;
        this.proxyUrl = (typeof myajax !== 'undefined' && myajax.proxy_url) || '/wp-content/themes/gruuun-theme/api-proxi.php';

        this.contractNumber = '';   // v
        this.meterNumberFromLink = ''; // z

        this.meterType = 'single';   // 'single' | 'htnt'
        this.registerTemplates = []; // [{ registerId, obis }]
        this.periodStart = null;     // Date | null
        this.periodEnd = null;       // Date | null
        this.lastReading = '';
        this.lastReadingDate = '';

        // Value entered on the meter form, carried into the photo e-mail (spec 17/22).
        this.enteredMeterValue = '';

        // Photo-upload sub-page (Phase 4)
        this.uploadVariant = null;   // 'nicht_aktuell' | 'unplausibel'
        this.uploadedFiles = [];
        this.uploadedPhotos = [];

        this.readUrlParams();
        this.initGlobalListeners();
        this.render();
    }

    get loading() { return this._loading; }
    set loading(value) {
        if (this._loading !== value) {
            this._loading = value;
            if (typeof this.onLoadingChange === 'function') this.onLoadingChange(value);
        }
    }

    readUrlParams() {
        const params = new URLSearchParams(window.location.search);
        this.contractNumber = (params.get('v') || '').trim();
        this.meterNumberFromLink = (params.get('z') || '').trim();
    }

    showLoader() { $(SELECTORS.LOADER).show(); }
    hideLoader() { $(SELECTORS.LOADER).hide(); }

    initGlobalListeners() {
        $(document).on('keyup blur change', SELECTORS.ALL_INPUTS, (e) => this.handleInputValidation(e));
        $(document).on('input', SELECTORS.NINJA_FORMS_FIELD, (e) => this.handleInputEvent(e));
        $(document).on('focus', SELECTORS.NINJA_FORMS_FIELD, (e) => this.handleFocusEvent(e));
        $(document).on('blur', SELECTORS.NINJA_FORMS_FIELD, (e) => this.handleBlurEvent(e));
        $(document).on('input change keyup', SELECTORS.FORM + ' ' + SELECTORS.ALL_INPUTS, () => this.refreshSubmitState());
        $(document).on('submit', SELECTORS.FORM, this.handleSubmit.bind(this));

        // Date field — multi-step form parity (section_multi-step_form.es6):
        // clicking the floating label focuses the visible (alt)input, and the
        // visible input gets a dd.mm.yyyy mask while it is typed into.
        $(document).on('click', '.date-container label', (e) => {
            e.preventDefault();
            let $target = $('#' + $(e.currentTarget).attr('for'));
            if ($target.attr('type') === 'hidden') $target = $target.next();
            $target.trigger('focus');
        });
        $(document).on('focus', '.date-container .input', function () {
            if ($.fn.inputmask) {
                $(this).inputmask({ mask: '99.99.9999', placeholder: 'tt.mm.jjjj', clearIncomplete: true });
            }
        });
        $(document).on('blur', '.date-container .input', function () {
            if ($.fn.inputmask) $(this).inputmask('remove');
            $(this).attr('placeholder', '');
        });

        // Flow popups (Phase 2)
        $(document).on('click', '.zaehlerstand-popup .modal-overlay, .zaehlerstand-popup .close-button', (e) => {
            const $popup = $(e.currentTarget).closest('.zaehlerstand-popup');
            if ($popup.attr('data-dismissable') === 'false') return;
            this.closePopup($popup.attr('id'));
        });
        $(document).on('click', '[data-action="confirm-plausibility"]', () => this.onConfirmPlausibility());
        $(document).on('click', '[data-action="correct-value"]', () => this.onCorrectValue());
        $(document).on('click', '[data-action="meterchange-yes"]', () => this.onMeterChange(true));
        $(document).on('click', '[data-action="meterchange-no"]', () => this.onMeterChange(false));

        // Spec step 7a: user self-reports a wrong meter number from the form.
        $(document).on('click', SELECTORS.FORM + ' .wrong-meter-opener', (e) => {
            e.preventDefault();
            this.openMeterChangePopup('mismatch');
        });

        // Photo-upload sub-page (Phase 4)
        $(document).on('submit', SELECTORS.UPLOAD_FORM, this.handleUploadSubmit.bind(this));
        $(document).on('input change keyup', SELECTORS.UPLOAD_FORM + ' ' + SELECTORS.ALL_INPUTS,
            () => this.refreshUploadSubmitState());

        this.onLoadingChange = (isLoading) => {
            $(SELECTORS.SUBMIT_BUTTON).prop('disabled', isLoading || !this.allFieldsFilled());
            $(SELECTORS.SUBMIT_BUTTON).toggleClass('-loading', isLoading);
        };
    }

    /* ---------------------------------------------------------------- flow */

    render() {
        if (!this.contractNumber || !this.meterNumberFromLink) {
            this.renderMessage(CFG.error_missing_params ||
                'Dieser Link ist unvollständig. Bitte nutze den personalisierten Link aus Deinem Schreiben.');
            return;
        }

        this.loading = true;
        this.showLoader();

        Promise.all([this.getPossibleMeters(), this.loadContract(), this.getLastMeterReadings()])
            .then(([metersResponse, , lastReadingsResponse]) => {
                this.parseMeters(metersResponse);
                this.applyLastReading(lastReadingsResponse);

                const matches = this.normalizeMeterNo(this.meterNumberFromLink) ===
                    this.normalizeMeterNo(this.apiMeterNumber);

                if (!matches) {
                    // Spec step 4: meter numbers differ -> "Zählerwechsel?" popup.
                    // Intro stays hidden: the popup is the only thing shown here.
                    this.hideLoader();
                    this.loading = false;
                    this.openMeterChangePopup('mismatch');
                    return null;
                }

                // Loader (and the intro) stay up/hidden until the form itself is
                // rendered - renderForm() reveals both once the
                // get_zaehlerstand_form markup is actually injected.
                return this.renderForm();
            })
            .catch((error) => {
                console.error('[Zaehlerstand] init failed:', error);
                this.renderMessage(CFG.error_generic ||
                    'Leider konnten wir Deine Zählerdaten nicht laden. Bitte versuche es später erneut.');
                this.hideLoader();
                this.loading = false;
            });
    }

    getPossibleMeters() {
        return $.ajax({
            url: `${this.proxyUrl}?type=getPossibleMetersByContractNumber`,
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({ contractNumber: this.contractNumber }),
        });
    }

    getLastMeterReadings() {
        // "Letzter Stand" line under the meter field; a failure here must not block the flow.
        return $.ajax({
            url: `${this.proxyUrl}?type=listMeterReadingByContractNumber`,
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({ contractNumber: this.contractNumber }),
        }).catch(() => null);
    }

    loadContract() {
        // periodStart / periodEnd bound the Ablesedatum range; a failure here must not block the flow.
        return $.ajax({
            url: `${this.proxyUrl}?type=getContractBasicInfo`,
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({ contractNumber: this.contractNumber }),
        }).then((res) => {
            const raw = typeof res === 'string' ? this.safeJson(res) : res;
            const contract = raw && (raw.data || raw);
            this.periodStart = this.berlinDay(contract && contract.periodStart);
            this.periodEnd = this.berlinDay(contract && contract.periodEnd);
            return raw;
        }).catch(() => null);
    }

    parseMeters(response) {
        const raw = typeof response === 'string' ? this.safeJson(response) : response;
        let meter = raw;

        if (Array.isArray(raw)) meter = raw[0];
        else if (raw && Array.isArray(raw.meters)) meter = raw.meters[0];
        else if (raw && Array.isArray(raw.data)) meter = raw.data[0];

        if (!meter || typeof meter !== 'object') {
            throw new Error('Unexpected getPossibleMetersByContractNumber response');
        }

        this.apiMeterNumber = String(meter.number || meter.meterNumber || '').trim();

        const registers = meter.registerValues || meter.registers || meter.registerList || [];
        const obisOf = (r) => String(r.obis || '').trim();
        const idOf = (r) => String(r.registerId || r.id || '').trim();
        const isObis = (r, code) => obisOf(r).endsWith(':' + code) || obisOf(r) === code || obisOf(r).endsWith('.' + code);

        const single = registers.find((r) => isObis(r, '1.8.0'));
        const ht = registers.find((r) => isObis(r, '1.8.1'));
        const nt = registers.find((r) => isObis(r, '1.8.2'));

        if (ht || nt) {
            this.meterType = 'htnt';
            this.registerTemplates = [ht, nt].filter(Boolean).map((r) => ({ registerId: idOf(r), obis: obisOf(r) }));
        } else if (single) {
            this.meterType = 'single';
            this.registerTemplates = [{ registerId: idOf(single), obis: obisOf(single) }];
        } else if (registers.length) {
            // fallback: use whatever single register we got
            this.meterType = 'single';
            this.registerTemplates = [{ registerId: idOf(registers[0]), obis: obisOf(registers[0]) }];
        } else {
            throw new Error('No OBIS registers in getPossibleMetersByContractNumber response');
        }
    }

    /**
     * "Letzter Stand: {value} kWh vom {date}" — from
     * /proxy/portal/meterreading/listMeterReadingByContractNumber, not from
     * getPossibleMetersByContractNumber (which carries no history/date).
     * Response is an array of past readings; each has a top-level `date`
     * (unix ms) and `registerValues[]` (obis + value). Must run after
     * parseMeters() so this.registerTemplates (current meter's OBIS codes)
     * is already known.
     */
    applyLastReading(response) {
        this.lastReading = '';
        this.lastReadingDate = '';

        const raw = typeof response === 'string' ? this.safeJson(response) : response;
        const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.data) ? raw.data : []);
        if (!list.length) return;

        const latest = list.reduce((a, b) => (Number(b.date) || 0) > (Number(a.date) || 0) ? b : a);
        const registerValues = latest.registerValues || [];
        const wantedObis = this.registerTemplates.map((t) => t.obis);
        const entry = registerValues.find((r) => wantedObis.includes(String(r.obis || '').trim())) || registerValues[0];

        if (entry && entry.value !== null && entry.value !== undefined && entry.value !== '') {
            this.lastReading = String(entry.value).trim();
            this.lastReadingDate = this.formatApiDateDE(latest.date);
        }
    }

    renderForm() {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: myajax.url,
                method: 'POST',
                data: {
                    action: 'get_zaehlerstand_form',
                    nonce: myajax.nonces.get_zaehlerstand_form,
                    page_id: this.pageId(),
                    meter_type: this.meterType,
                    contract_number: this.contractNumber,
                    meter_number: this.meterNumberFromLink,
                    last_reading: this.lastReading,
                    last_reading_date: this.lastReadingDate,
                },
                success: (response) => {
                    if (response && response.data && response.data.form) {
                        $(SELECTORS.FORM_CONTAINER).html(response.data.form);
                        $('.zaehlerstand-intro').prop('hidden', false);
                        this.hideLoader();
                        this.loading = false;
                        this.setupForm();
                        resolve();
                    } else {
                        reject(new Error('Empty form response'));
                    }
                },
                error: (xhr, status, error) => reject(error),
            });
        });
    }

    setupForm() {
        this.setupDatePicker();
        this.setupValidation();
        this.markFields();
        this.refreshSubmitState();
    }

    /* ------------------------------------------------------------- submit */

    handleSubmit(event) {
        event.preventDefault();
        if (this.loading) return;

        const $form = $(SELECTORS.FORM);
        if (!$form.valid()) return;

        if (!this.registerTemplates.length) {
            this.showError(CFG.error_generic ||
                'Leider konnten wir Deine Zählerdaten nicht laden. Bitte versuche es später erneut.');
            return;
        }

        this.captureEnteredMeterValue();

        this.loading = true;
        this.clearErrors();

        this.createMeterReading(false)
            .then((response) => this.handleCreateResponse(response, false))
            .catch((error) => {
                console.error('[Zaehlerstand] createMeterReading failed:', error);
                this.showError(CFG.error_submit ||
                    'Deine Meldung konnte nicht übermittelt werden. Bitte versuche es später erneut.');
                this.loading = false;
            });
    }

    createMeterReading(forceSave) {
        const body = {
            contractNumber: this.contractNumber,
            registerValues: this.buildRegisterValues(),
            date: this.ablesedatumIso(),
        };
        if (forceSave) body.forceSave = true;
        else body.forceSave = false;

        return $.ajax({
            url: `${this.proxyUrl}?type=createMeterReading`,
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify(body),
        });
    }

    buildRegisterValues() {
        if (this.meterType === 'htnt') {
            const ht = this.registerTemplates[0] || {};
            const nt = this.registerTemplates[1] || {};
            return [
                { registerId: ht.registerId, obis: ht.obis, value: this.numeric($(SELECTORS.METER_READING_HT_INPUT).val()) },
                { registerId: nt.registerId, obis: nt.obis, value: this.numeric($(SELECTORS.METER_READING_NT_INPUT).val()) },
            ];
        }
        const single = this.registerTemplates[0] || {};
        return [
            { registerId: single.registerId, obis: single.obis, value: this.numeric($(SELECTORS.METER_READING_INPUT).val()) },
        ];
    }

    handleCreateResponse(response, wasForced) {
        const raw = typeof response === 'string' ? this.safeJson(response) : response;
        const messages = Array.isArray(raw) ? raw : [];

        // Success: MBS returns an empty array.
        if (messages.length === 0) {
            this.loading = false;
            if (wasForced) {
                // forceSave path: proceed to the "Zählerwechsel?" question (spec 12a).
                this.openMeterChangePopup('plausibility');
            } else {
                this.redirectToSuccessManual();
            }
            return;
        }

        const notPlausible = messages.some((m) => m && typeof m.errorMessage === 'string'
            && /notplausible/i.test(m.errorMessage));

        // MBS validation crashes when there's no reading one year back (new contracts):
        // "Error.MeterReading.errorDuringValidation RuntimeException: No MeterReading found for <date>".
        // Agreed with the client: silently repeat with forceSave = true and go to the success page.
        const noHistoricReading = messages.some((m) => m && typeof m.errorMessage === 'string'
            && /Error\.MeterReading\.errorDuringValidation RuntimeException: No MeterReading found/i.test(m.errorMessage));

        if (!wasForced && noHistoricReading) {
            this.retryWithForceSave();
            return;
        }

        this.loading = false;

        if (!wasForced && notPlausible) {
            // Spec step 12: "Bist Du Dir sicher, dass der Zählerstand korrekt ist?"
            this.openPlausibilityPopup();
            return;
        }

        if (wasForced && (notPlausible || messages.length)) {
            // forceSave still returned messages -> proceed anyway per spec 12a.
            this.openMeterChangePopup('plausibility');
            return;
        }

        console.warn('[Zaehlerstand] createMeterReading returned messages:', messages);
        this.showError(CFG.error_not_plausible ||
            'Der eingegebene Zählerstand konnte nicht übermittelt werden. Bitte prüfe Deine Eingabe.');
    }

    retryWithForceSave() {
        this.createMeterReading(true)
            .then((response) => {
                const raw = typeof response === 'string' ? this.safeJson(response) : response;
                const messages = Array.isArray(raw) ? raw : [];
                if (messages.length === 0) {
                    this.redirectToSuccessManual();
                    return;
                }
                console.warn('[Zaehlerstand] createMeterReading (forceSave retry) returned messages:', messages);
                this.loading = false;
                this.showError(CFG.error_not_plausible ||
                    'Der eingegebene Zählerstand konnte nicht übermittelt werden. Bitte prüfe Deine Eingabe.');
            })
            .catch((error) => {
                console.error('[Zaehlerstand] createMeterReading (forceSave retry) failed:', error);
                this.showError(CFG.error_submit ||
                    'Deine Meldung konnte nicht übermittelt werden. Bitte versuche es später erneut.');
                this.loading = false;
            });
    }

    redirectToSuccess() {
        window.location.href = CFG.success_page || '/zaehlerstand-gemeldet';
    }

    // Direct entry (spec step 11) — separate target page from the upload flow;
    // carries the submitted value/date as URL params since it's a fresh page load.
    redirectToSuccessManual() {
        const base = CFG.success_page_manual || CFG.success_page || '/zaehlerstand-gemeldet';
        const params = new URLSearchParams();
        if (this.enteredMeterValue) params.set('zst_value', this.enteredMeterValue);
        const date = this.ablesedatumDE();
        if (date) params.set('zst_date', date);
        const query = params.toString();
        const sep = base.indexOf('?') === -1 ? '?' : '&';
        window.location.href = query ? `${base}${sep}${query}` : base;
    }

    /* ------------------------------------------------------------- popups */

    openPopup(id) {
        $('#' + id).addClass('-open').attr('aria-hidden', 'false');
        $('body').addClass('overflow-hidden');
    }

    closePopup(id) {
        if (!id) return;
        $('#' + id).removeClass('-open').attr('aria-hidden', 'true');
        $('body').removeClass('overflow-hidden');
    }

    closeAllPopups() {
        $('.zaehlerstand-popup').removeClass('-open').attr('aria-hidden', 'true');
        $('body').removeClass('overflow-hidden');
    }

    openPlausibilityPopup() {
        let displayValue;
        if (this.meterType === 'htnt') {
            const ht = this.formatValueDE(this.numeric($(SELECTORS.METER_READING_HT_INPUT).val()));
            const nt = this.formatValueDE(this.numeric($(SELECTORS.METER_READING_NT_INPUT).val()));
            displayValue = `HT ${ht} kWh / NT ${nt} kWh`;
        } else {
            displayValue = `${this.formatValueDE(this.numeric($(SELECTORS.METER_READING_INPUT).val()))} kWh`;
        }

        $('#zaehlerstand-popup-plausibility .zst-popup-value').text(displayValue);
        $('#zaehlerstand-popup-plausibility .zst-popup-date').text(this.ablesedatumDE());

        this.openPopup('zaehlerstand-popup-plausibility');
    }

    openMeterChangePopup(context) {
        this.meterChangeContext = context; // 'plausibility' | 'mismatch'
        this.closePopup('zaehlerstand-popup-plausibility');
        this.openPopup('zaehlerstand-popup-meterchange');
    }

    onConfirmPlausibility() {
        // "Der Zählerstand ist korrekt" -> repeat with forceSave = true (spec 12a).
        if (this.loading) return;
        this.loading = true;
        this.closePopup('zaehlerstand-popup-plausibility');

        this.createMeterReading(true)
            .then((response) => this.handleCreateResponse(response, true))
            .catch((error) => {
                console.error('[Zaehlerstand] forced createMeterReading failed:', error);
                this.loading = false;
                this.showError(CFG.error_submit ||
                    'Deine Meldung konnte nicht übermittelt werden. Bitte versuche es später erneut.');
            });
    }

    onCorrectValue() {
        // "Zählerstand korrigieren" -> back to the form (spec step 5).
        this.closePopup('zaehlerstand-popup-plausibility');
    }

    onMeterChange(hadChange) {
        this.closePopup('zaehlerstand-popup-meterchange');

        if (this.meterChangeContext === 'plausibility') {
            // Spec 12a: Ja -> upload page (step 13, "Zählernummer nicht aktuell" mail);
            //           Nein -> other upload page (step 18, "Zählerstand unplausibel" mail).
            this.enterUploadFlow(hadChange ? 'nicht_aktuell' : 'unplausibel');
        } else {
            // Spec step 4 / 7a (mismatch context):
            //   Ja -> upload page (step 13), Nein -> error screen (step 4b).
            if (hadChange) {
                this.enterUploadFlow('nicht_aktuell');
            } else {
                this.showMismatchError();
            }
        }
    }

    showMismatchError() {
        this.closeAllPopups();
        $('.zaehlerstand-intro').prop('hidden', true);
        $(SELECTORS.FORM_CONTAINER).empty();
        $('#zaehlerstand-mismatch-error').prop('hidden', false);
    }

    /* ------------------------------------------------- photo upload (13-22) */

    enterUploadFlow(variant) {
        this.uploadVariant = variant === 'unplausibel' ? 'unplausibel' : 'nicht_aktuell';
        this.uploadedFiles = [];
        this.uploadedPhotos = [];
        this.closeAllPopups();
        $('.zaehlerstand-intro').prop('hidden', true);
        $(SELECTORS.FORM_CONTAINER).empty();
        $('#zaehlerstand-mismatch-error').prop('hidden', true);
        this.showLoader();
        this.loading = true;

        $.ajax({
            url: myajax.url,
            method: 'POST',
            data: {
                action: 'get_zaehlerstand_upload_form',
                nonce: myajax.nonces.get_zaehlerstand_upload_form,
                page_id: this.pageId(),
                variant: this.uploadVariant,
            },
        }).then((response) => {
            if (!(response && response.data && response.data.form)) {
                throw new Error('Empty upload-form response');
            }
            $(SELECTORS.FORM_CONTAINER).html(response.data.form);
            $('#zaehlerstand-mismatch-error').prop('hidden', true);
            this.hideLoader();
            this.loading = false;
            this.initUploadForm();
        }).catch((error) => {
            console.error('[Zaehlerstand] upload form load failed:', error);
            this.hideLoader();
            this.loading = false;
            this.renderMessage(CFG.error_upload_form_load ||
                'Leider ist ein Fehler aufgetreten. Bitte versuche es später erneut.');
        });
    }

    initUploadForm() {
        this.setupDatePicker();
        this.initializeUploader();
        this.initializeMobUploader();
        this.markFields();
        this.refreshUploadSubmitState();
    }

    isUploadDateValid() {
        const value = $(SELECTORS.FOTODATUM_INPUT).val();
        if (!value) return false;
        return this.isAblesedatumInRange(value);
    }

    refreshUploadSubmitState() {
        const ready = this.isUploadDateValid() && this.uploadedFiles.length >= 1;
        $(SELECTORS.UPLOAD_FORM + ' ' + SELECTORS.SUBMIT_BUTTON).prop('disabled', this.loading || !ready);
    }

    handleUploadSubmit(event) {
        event.preventDefault();
        if (this.loading) return;

        this.clearErrors();

        if (!this.isUploadDateValid()) {
            this.showError(CFG.error_upload_date ||
                'Bitte wähle ein gültiges Datum.');
            return;
        }

        if (this.uploadedFiles.length < 1) {
            this.showError(CFG.upload_min_images_error || 'Bitte lade mindestens 1 Bild hoch.');
            return;
        }

        this.loading = true;
        $(SELECTORS.UPLOAD_FORM + ' ' + SELECTORS.SUBMIT_BUTTON).prop('disabled', true).addClass('-loading');

        this.sendUploadEmail()
            .then(() => this.redirectToSuccess())
            .catch((error) => {
                console.error('[Zaehlerstand] upload e-mail failed:', error);
                this.loading = false;
                $(SELECTORS.UPLOAD_FORM + ' ' + SELECTORS.SUBMIT_BUTTON).removeClass('-loading');
                this.refreshUploadSubmitState();
                this.showError(CFG.error_upload_submit ||
                    'Deine Angaben konnten nicht übermittelt werden. Bitte versuche es später erneut.');
            });
    }

    sendUploadEmail() {
        const formData = new FormData();
        formData.append('action', 'send_meter_reading_email');
        formData.append('nonce', myajax.nonces.send_meter_reading_email);
        formData.append('page_id', this.pageId());
        formData.append('mail_type', this.uploadVariant);
        formData.append('contract_number', this.contractNumber);
        formData.append('meter_reading', this.enteredMeterValue || '');
        formData.append('ablesedatum', this.ablesedatumDE(SELECTORS.FOTODATUM_INPUT));

        this.uploadedFiles.forEach((file, index) => {
            formData.append(`file_${index}`, file.data, file.name);
        });

        return new Promise((resolve, reject) => {
            $.ajax({
                url: myajax.url,
                method: 'POST',
                data: formData,
                processData: false,
                contentType: false,
                success: (res) => (res && res.success ? resolve(res) : reject(new Error('mail not sent'))),
                error: () => reject(new Error('mail request failed')),
            });
        });
    }

    captureEnteredMeterValue() {
        if (this.meterType === 'htnt') {
            const ht = this.formatValueDE(this.numeric($(SELECTORS.METER_READING_HT_INPUT).val()));
            const nt = this.formatValueDE(this.numeric($(SELECTORS.METER_READING_NT_INPUT).val()));
            this.enteredMeterValue = `HT ${ht} kWh / NT ${nt} kWh`;
        } else {
            const v = this.formatValueDE(this.numeric($(SELECTORS.METER_READING_INPUT).val()));
            this.enteredMeterValue = v ? `${v} kWh` : '';
        }
    }

    /* ------------------------------------------------------- uploader (ad-campaign port) */

    initializeUploader() {
        const popup = document.getElementById('popup-file-uploader-desktop');
        const dropZone = document.getElementById('drop-zone');
        const fileInput = document.getElementById('file-input');
        if (!popup || !dropZone || !fileInput) return;
        const uploadArea = popup.querySelector('.upload-area');

        dropZone.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', (e) => {
            if (e.target.files.length > 0) this.handleUploadedFile(e.target.files[0]);
        });

        ['dragenter', 'dragover'].forEach((eventName) => {
            popup.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (popup.classList.contains('-open') && !uploadArea.classList.contains('-hidden')) {
                    dropZone.classList.add('dragover');
                }
            });
        });
        ['dragleave', 'drop'].forEach((eventName) => {
            popup.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
                dropZone.classList.remove('dragover');
            });
        });
        popup.addEventListener('drop', (e) => {
            e.preventDefault();
            if (e.dataTransfer.files.length > 0) this.handleUploadedFile(e.dataTransfer.files[0]);
        });
    }

    initializeMobUploader() {
        const fileInputMob = document.getElementById('file-input-mob');
        const photoInputMob = document.getElementById('file-photo-mob');
        const addPhoto = document.getElementById('add-photo');
        if (!fileInputMob) return;

        fileInputMob.addEventListener('change', (e) => {
            if (e.target.files.length > 0) this.handleUploadedFileMob(e.target.files[0]);
        });
        photoInputMob.addEventListener('change', (e) => {
            if (e.target.files.length > 0) this.handleUploadedPhotoMob(e.target.files[0]);
        });
        addPhoto.addEventListener('click', () => photoInputMob.click());
    }

    uploadFileError(scope, message, hint) {
        $(`#${scope} .response-errors-uploads`).html(`<div class="error">${message}</div><div class="hint">${hint}</div>`);
    }

    uploadTypeError(scope) {
        this.uploadFileError(scope,
            CFG.error_file_type || 'Dateiformat nicht erkannt',
            CFG.error_file_type_hint || 'Bitte versuche es mit einer anderen Datei.');
    }

    uploadSizeError(scope) {
        this.uploadFileError(scope,
            CFG.error_file_size || 'Deine Datei ist zu groß',
            CFG.error_file_size_hint || 'Die Datei darf nicht größer als 20 MB sein.');
    }

    handleUploadedFile(file) {
        const $errors = $('#popup-file-uploader-desktop .response-errors-uploads');
        if (!UPLOAD_ALLOWED_TYPES.includes(file.type)) {
            this.uploadTypeError('popup-file-uploader-desktop');
            return;
        }
        if (file.size > UPLOAD_MAX_SIZE) {
            this.uploadSizeError('popup-file-uploader-desktop');
            return;
        }
        $errors.empty();
        this.runUploadProgress(file, 'desktop', false, () => {
            this.uploadedFiles.push({ name: file.name, size: file.size, data: file });
            this.hideUploadProgress('desktop');
            this.renderUploadedFilesUI();
        });
        this.clearErrors();
    }

    handleUploadedFileMob(file) {
        if (!UPLOAD_ALLOWED_TYPES.includes(file.type)) {
            this.uploadTypeError('popup-file-uploader-mobile');
            return;
        }
        if (file.size > UPLOAD_MAX_SIZE) {
            this.uploadSizeError('popup-file-uploader-mobile');
            return;
        }
        $('#popup-file-uploader-mobile .response-errors-uploads').empty();
        this.runUploadProgress(file, 'mobile', false, () => {
            this.uploadedFiles.push({ name: file.name, size: file.size, data: file });
            this.hideUploadProgress('mobile');
            this.renderUploadedFilesUI();
        });
        this.clearErrors();
    }

    handleUploadedPhotoMob(file) {
        if (!file || !(file instanceof File) || file.size === 0 || !file.type || !file.name
            || !UPLOAD_ALLOWED_TYPES.includes(file.type)) {
            this.uploadTypeError('popup-file-uploader-mobile');
            return;
        }
        if (file.size > UPLOAD_MAX_SIZE) {
            this.uploadSizeError('popup-file-uploader-mobile');
            return;
        }
        $('#popup-file-uploader-mobile .response-errors-uploads').empty();
        this.runUploadProgress(file, 'mobile', true, () => {
            this.uploadedPhotos.push({ name: file.name, size: file.size, data: file });
            this.showPhotoArea();
        });
    }

    runUploadProgress(file, device, isPhoto, done) {
        this.showUploadProgress(file, device, isPhoto);
        const total = file.size;
        const totalKB = (total / 1024).toFixed(0);
        const fileSizeKB = file.size / 1024;
        let duration = 500 + ((fileSizeKB - 1) * (3000 - 500)) / (20480 - 1);
        if (fileSizeKB <= 1) duration = 500;
        else if (fileSizeKB >= 20480) duration = 3000;

        const steps = 100;
        const interval = duration / steps;
        let step = 0;
        const timer = setInterval(() => {
            step++;
            const percent = Math.min((step / steps) * 100, 100);
            const loadedKB = (Math.floor((percent / 100) * total) / 1024).toFixed(0);
            $('.file-uploading .file-size').text(`${loadedKB} KB von ${totalKB} KB`);
            $('.progress-line .filled').css('width', `${percent}%`);
            if (step >= steps) {
                clearInterval(timer);
                done();
                this.refreshUploadSubmitState();
            }
        }, interval);
    }

    showUploadProgress(file, device, photo) {
        const $popup = device === 'desktop' ? $('#popup-file-uploader-desktop') : $('#popup-file-uploader-mobile');
        const $fileUploadingBox = $popup.find('.file-uploading');
        $fileUploadingBox.toggleClass('-photo', !!photo);
        $popup.find('.file-uploading .file-info').html(
            `<div class="file-name text-3">${file.name}</div>` +
            `<div class="file-size text-3">0 KB von ${(file.size / 1024).toFixed(0)} KB</div>`
        );
        if (!$popup.find('.progress-area').hasClass('-visible')) {
            $popup.find('.upload-area').addClass('-hidden');
            $popup.find('.progress-area').addClass('-visible');
            $popup.find('.photo-area').removeClass('-visible');
        }
    }

    hideUploadProgress(device) {
        const $popup = device === 'desktop' ? $('#popup-file-uploader-desktop') : $('#popup-file-uploader-mobile');
        $popup.removeClass('-open');
        $('body').removeClass('overflow-hidden');
        $popup.find('.file-uploading .file-info').empty();
        $popup.find('.progress-line .filled').css('width', '0%');
        $popup.find('.upload-area').removeClass('-hidden');
        $popup.find('.progress-area').removeClass('-visible');
        $popup.find('.photo-area').removeClass('-visible');
    }

    showPhotoArea() {
        const $popup = $('#popup-file-uploader-mobile');
        const $container = $('.photos-list');
        const countDiv = $popup.find('.photos-count');
        $container.find('.item-default').remove();

        if (this.uploadedPhotos.length > 0) {
            countDiv.html(this.uploadedPhotos.length > 1
                ? `(${this.uploadedPhotos.length} Fotos)` : `(${this.uploadedPhotos.length} Foto)`);
            this.uploadedPhotos.forEach((file, index) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const html = `<div class="item-default" data-index="${index}" style="background-image: url('${e.target.result}')">` +
                        `<button class="delete-photo" data-index="${index}"></button></div>`;
                    $container.append(html);
                };
                reader.readAsDataURL(file.data);
            });
            $popup.find('.upload-area').addClass('-hidden');
            $popup.find('.progress-area').removeClass('-visible');
            $popup.find('.photo-area').addClass('-visible');
        } else {
            $popup.find('.upload-area').removeClass('-hidden');
            $popup.find('.progress-area').removeClass('-visible');
            $popup.find('.photo-area').removeClass('-visible');
        }
    }

    pushPhotos() {
        if (!Array.isArray(this.uploadedPhotos) || this.uploadedPhotos.length === 0) return;
        this.uploadedPhotos.forEach((photo) => this.uploadedFiles.push(photo));
        this.uploadedPhotos = [];
        this.hideUploadProgress('mobile');
        this.renderUploadedFilesUI();
        this.refreshUploadSubmitState();
        const $popup = $('#popup-file-uploader-mobile');
        $popup.removeClass('-open');
        $('body').removeClass('overflow-hidden');
        $popup.find('.upload-area').removeClass('-hidden');
        $popup.find('.progress-area').removeClass('-visible');
        $popup.find('.photo-area').removeClass('-visible');
    }

    renderUploadedFilesUI() {
        const initBox = $('.initial-upload-box');
        const hiddenBox = $('.hidden-upload-box');
        const container = $('.uploaded-files-list');
        container.empty();

        if (this.uploadedFiles.length > 0) {
            this.uploadedFiles.forEach((file, index) => {
                container.append(
                    `<div class="upload-entry" data-index="${index}">` +
                    `<div class="file-name text-3">${file.name}</div>` +
                    `<div class="file-size text-3">${(file.size / 1024).toFixed(0)} KB</div>` +
                    `<button class="delete-upload" data-index="${index}"></button></div>`
                );
            });
            initBox.addClass('-hidden');
            hiddenBox.addClass('-visible');
        } else {
            initBox.removeClass('-hidden');
            hiddenBox.removeClass('-visible');
        }
        this.refreshUploadSubmitState();
    }

    /* --------------------------------------------------------- date logic */

    // Latest allowed date: today, or periodEnd if the contract period already ended.
    maxAblesedatum() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (this.periodEnd && this.periodEnd < today) return new Date(this.periodEnd);
        return today;
    }

    // Earliest allowed date: 30 days before maxAblesedatum(), but never before periodStart.
    minAblesedatum() {
        const max = this.maxAblesedatum();
        const minus30 = new Date(max);
        minus30.setDate(max.getDate() - 30);

        if (this.periodStart && this.periodStart > minus30) return new Date(this.periodStart);
        return minus30;
    }

    // False when periodStart lies after the latest allowed date — no date can be selected.
    hasSelectableAblesedatum() {
        return this.minAblesedatum() <= this.maxAblesedatum();
    }

    isAblesedatumInRange(value) {
        const selected = this.parseYmd(value);
        if (!selected) return false;
        return selected >= this.minAblesedatum() && selected <= this.maxAblesedatum();
    }

    setupDatePicker() {
        if (!this.hasSelectableAblesedatum()) {
            $(SELECTORS.DATE_PICKER).prop('disabled', true);
            this.showError(CFG.error_no_valid_date ||
                'Für Deinen Vertrag ist aktuell keine Zählerstandsmeldung möglich. Bitte wende Dich an unseren Kundenservice.');
            return;
        }

        const minDate = this.minAblesedatum();
        const maxDate = this.maxAblesedatum();

        const onDateChange = () => this.onDatePickerChange();

        setTimeout(() => {
            $(SELECTORS.DATE_PICKER).each(function () {
                const $el = $(this);
                const fp = $el.flatpickr({
                    locale: German,
                    altInput: true,
                    altFormat: 'd.m.Y',
                    dateFormat: 'Y-m-d',
                    allowInput: true,
                    clickOpens: false,
                    disableMobile: true,
                    minDate: minDate,
                    maxDate: maxDate,
                    onValueUpdate: onDateChange,
                    onClose: onDateChange,
                });
                $el.closest(SELECTORS.FIELD_CONTAINER).find(SELECTORS.DATE_ICON).on('click', () => fp.open());

                // flatpickr's altInput is the element the user actually sees and
                // focuses, but it is not a `.ninja-forms-field`, so the shared
                // label/validation handlers (focus/blur/input) would skip it.
                // Tag it so it behaves exactly like a multi-step form date field.
                if (fp && fp.altInput) {
                    fp.altInput.classList.add('ninja-forms-field');
                    if ($el.prop('required')) fp.altInput.setAttribute('required', 'required');
                    $el.closest(SELECTORS.FIELD_CONTAINER).addClass('active-label');
                }
            });
        }, 10);
    }

    onDatePickerChange() {
        $(SELECTORS.DATE_PICKER).each(function () {
            const $c = $(this).closest(SELECTORS.FIELD_CONTAINER);
            $c.toggleClass('label-up', String($(this).val() || '').trim() !== '');
        });
        if ($(SELECTORS.FORM).length) {
            $(SELECTORS.ABLESEDATUM_INPUT).valid();
        } else if ($(SELECTORS.UPLOAD_FORM).length) {
            this.refreshUploadSubmitState();
        }
    }

    ablesedatumIso() {
        const value = $(SELECTORS.ABLESEDATUM_INPUT).val(); // Y-m-d
        // NOTE: user portal sends "<date>T02:00:00.000Z" — replicated verbatim.
        // TODO(open question #): confirm against the actual user-portal implementation.
        return `${value}T02:00:00.000Z`;
    }

    /* --------------------------------------------------------- validation */

    setupValidation() {
        $.extend($.validator.messages, {
            required: CFG.error_required || 'Dieses Feld wird benötigt.',
            digits: CFG.error_digits || 'Bitte gib den Zählerstand ohne Nachkommastellen ein.',
        });

        const self = this;
        $.validator.addMethod('ablesedatumRange', function (value, element) {
            if (!value) return this.optional(element);
            return this.optional(element) || self.isAblesedatumInRange(value);
        }, CFG.error_date || 'Bitte wähle ein gültiges Ablesedatum.');

        $(SELECTORS.FORM).validate({
            ignore: '.datepicker.input',
            rules: {
                meterReading: { required: true, digits: true },
                meterReadingHT: { required: true, digits: true },
                meterReadingNT: { required: true, digits: true },
                ablesedatum: { required: true, ablesedatumRange: true },
            },
            errorPlacement: (error, element) => {
                error.appendTo(element.closest('.field-wrap').find('.nf-error-msg'));
            },
            highlight: (element) => {
                $(element).closest('.field-wrap').addClass('nf-error').removeClass('nf-pass');
            },
            unhighlight: (element) => {
                $(element).closest('.field-wrap').removeClass('nf-error');
            },
            success: (label, element) => {
                if ($(element).val() !== '') $(element).closest('.field-wrap').addClass('nf-pass');
            },
        });
    }

    allFieldsFilled() {
        const $form = $(SELECTORS.FORM);
        if (!$form.length) return false;
        let filled = true;
        $form.find(`${SELECTORS.ALL_INPUTS}[required]`).each(function () {
            if (String($(this).val() || '').trim() === '') filled = false;
        });
        return filled;
    }

    refreshSubmitState() {
        $(SELECTORS.SUBMIT_BUTTON).prop('disabled', this.loading || !this.allFieldsFilled());
    }

    /* --------------------------------------------- NF-style label helpers */

    handleInputValidation(event) {
        let $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(SELECTORS.FIELD_CONTAINER);
        const $wrap = $input.closest('.field-wrap');

        if ($input.attr('type') === 'hidden') $input = $input.next();
        if (!$input.length) return;

        const validator = $(SELECTORS.FORM).validate();
        if ($input[0]) validator.element($input[0]);

        if (($input.is('[type="text"]') || $input.is('[type="number"]') || $input.is('[type="tel"]')) && !$attr) {
            if (String($input.val()).trim() !== '') {
                $container.addClass('fake-nf-pass');
            } else {
                $container.removeClass('fake-nf-pass');
                $wrap.removeClass('nf-pass');
            }
        }
    }

    handleInputEvent(event) {
        const $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(SELECTORS.FIELD_CONTAINER);
        const value = typeof $input.val() === 'string' ? $input.val().trim() : '';

        $container.removeClass('potentially-required-label');
        if (value !== '') {
            $container.addClass('label-up');
            if (typeof $attr !== 'undefined' && $attr !== false) $container.removeClass('required-label');
        } else {
            $container.removeClass('label-up');
            if (typeof $attr !== 'undefined' && $attr !== false) $container.addClass('required-label');
        }
    }

    handleFocusEvent(event) {
        const $container = $(event.currentTarget).closest(SELECTORS.FIELD_CONTAINER);
        $container.addClass('label-focus').removeClass('potentially-required-label');
    }

    handleBlurEvent(event) {
        const $input = $(event.currentTarget);
        const $attr = $input.attr('required');
        const $container = $input.closest(SELECTORS.FIELD_CONTAINER);
        $container.removeClass('potentially-required-label label-focus');
        if (String($input.val()).trim() === '') {
            if (typeof $attr !== 'undefined' && $attr !== false) $container.addClass('required-label');
        } else if (typeof $attr !== 'undefined' && $attr !== false) {
            $container.removeClass('required-label');
        }
    }

    markFields() {
        const form = $(SELECTORS.FORM + ', ' + SELECTORS.UPLOAD_FORM);
        form.find(SELECTORS.ALL_INPUTS).each(function () {
            const $input = $(this);
            const $container = $input.closest(SELECTORS.FIELD_CONTAINER);
            $container.addClass('active-label');
            if (String($input.val() || '').trim() !== '') $container.addClass('label-up');
        });
    }

    /* -------------------------------------------------------------- utils */

    renderMessage(html) {
        $(SELECTORS.FORM_CONTAINER).html(`<div class="page-title error-token"><div class="text-2">${html}</div></div>`);
    }

    showError(message) {
        $(SELECTORS.RESPONSE_ERRORS).html(`<div class="error">${message}</div>`);
    }

    clearErrors() {
        $(SELECTORS.RESPONSE_ERRORS).empty();
    }

    pageId() {
        if (typeof myajax !== 'undefined' && myajax.page_id) return myajax.page_id;
        const match = (document.body.className || '').match(/page-id-(\d+)/);
        return match ? match[1] : (CFG.page_id || 0);
    }

    numeric(value) {
        const digits = String(value == null ? '' : value).replace(/[^\d]/g, '');
        return digits === '' ? null : parseInt(digits, 10);
    }

    normalizeMeterNo(value) {
        return String(value || '').replace(/\s+/g, '').toUpperCase();
    }

    safeJson(str) {
        try { return JSON.parse(str); } catch (e) { return null; }
    }

    formatDateDE(value) {
        if (!value) return '';
        const d = new Date(value);
        if (isNaN(d.getTime())) return String(value);
        const p = (n) => String(n).padStart(2, '0');
        return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
    }

    // API timestamp (unix ms) -> local midnight Date of the same calendar day in Europe/Berlin.
    berlinDay(value) {
        if (value === null || value === undefined || value === '') return null;
        const d = new Date(Number(value) || value);
        if (isNaN(d.getTime())) return null;
        const parts = {};
        new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Europe/Berlin',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(d).forEach((p) => { parts[p.type] = p.value; });
        return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
    }

    // "Y-m-d" (flatpickr dateFormat) -> local midnight Date.
    parseYmd(value) {
        const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
    }

    // API timestamps (unix ms) are end-of-day in German time (e.g. 23:59:59 CEST) —
    // format in Europe/Berlin so the day doesn't shift for viewers in other time zones.
    formatApiDateDE(value) {
        if (!value) return '';
        const d = new Date(Number(value) || value);
        if (isNaN(d.getTime())) return String(value);
        return new Intl.DateTimeFormat('de-DE', {
            timeZone: 'Europe/Berlin',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(d);
    }

    ablesedatumDE(selector) {
        return this.formatDateDE($(selector || SELECTORS.ABLESEDATUM_INPUT).val());
    }

    formatValueDE(value) {
        if (value === null || value === undefined || value === '') return '';
        return Number(value).toLocaleString('de-DE');
    }
}

const zstService = new ZaehlerstandMelden();

/* ------------------------------------- photo-upload popup handlers (Phase 4) */

$(document).on('click', '.upload-tooltip-opener:not(:disabled)', function (e) {
    e.preventDefault();
    const isMobileCameraCapable = () => {
        const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        return isTouch && hasCamera;
    };
    if (isMobileCameraCapable()) {
        $('#popup-file-uploader-mobile').addClass('-open');
    } else {
        $('#popup-file-uploader-desktop').addClass('-open');
    }
    $('body').addClass('overflow-hidden');
});

$(document).on('click', '.upload-popup .close-button, .upload-popup .modal-overlay', function () {
    $(this).closest('.upload-popup').removeClass('-open');
    $('body').removeClass('overflow-hidden');
});

$(document).on('click', '.delete-upload', function () {
    zstService.uploadedFiles.splice($(this).data('index'), 1);
    zstService.renderUploadedFilesUI();
});

$(document).on('click', '.delete-photo', function () {
    zstService.uploadedPhotos.splice($(this).data('index'), 1);
    zstService.showPhotoArea();
});

$(document).on('click', '#push-photos', function () {
    zstService.pushPhotos();
});
